import React from "react";
import { Boxes, Users, ChefHat, Wine, Check, Sparkles, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";

const META = {
  gestionale: { icon: Boxes, img: IMAGES.gestionale, accent: "#00E5FF" },
  team: { icon: Users, img: IMAGES.team, accent: "#80F2FF" },
  kitchen: { icon: ChefHat, img: IMAGES.kitchen, accent: "#FF5A36" },
  bar: { icon: Wine, img: IMAGES.bar, accent: "#FF8A70" },
};

function ProductCard({ product, index }) {
  const { t } = useLang();
  const meta = META[product.id];
  const Icon = meta.icon;

  return (
    <Reveal delay={index * 0.08}>
      <article
        data-testid={`product-card-${product.id}`}
        className="group relative rounded-3xl border border-white/10 bg-ink-surface/50 overflow-hidden hover:border-white/20 transition-colors duration-300 h-full flex flex-col"
      >
        <div className="relative h-60 md:h-72 overflow-hidden border-b border-white/10">
          <img
            src={meta.img}
            alt={`${product.name} — anteprima interfaccia`}
            loading="lazy"
            className="w-full h-full object-cover object-top opacity-95 group-hover:scale-[1.03] transition-transform duration-700"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink-surface to-transparent" />
          <div className="absolute top-4 left-4 flex items-center gap-3">
            <span
              className="grid place-items-center w-11 h-11 rounded-xl border backdrop-blur-md"
              style={{ borderColor: `${meta.accent}55`, background: `${meta.accent}22`, color: meta.accent }}
            >
              <Icon size={20} strokeWidth={1.8} />
            </span>
          </div>
          <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 bg-black/50 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
            {product.domain}
          </span>
        </div>

        <div className="p-7 md:p-9 flex flex-col flex-1">
          <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">{product.name}</h3>
          <p className="mt-2 font-display text-lg md:text-xl" style={{ color: meta.accent }}>
            {product.tagline}
          </p>
          <p className="mt-4 text-white/65 leading-relaxed">{product.description}</p>

          <div className="mt-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">
              {t.productsSection.featuresLabel}
            </p>
            <ul className="grid sm:grid-cols-2 gap-4">
              {product.features.map((f, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-0.5 shrink-0 grid place-items-center w-5 h-5 rounded-full" style={{ background: `${meta.accent}22`, color: meta.accent }}>
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <div>
                    <p className="text-white text-sm font-semibold leading-tight">{f.title}</p>
                    <p className="text-white/55 text-sm leading-snug mt-1">{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 grid gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                {t.productsSection.whyLabel}
              </p>
              <p className="text-white/75 text-sm leading-relaxed">{product.whyMatters}</p>
            </div>
            <div
              className="rounded-2xl border p-5 relative overflow-hidden"
              style={{ borderColor: `${meta.accent}40`, background: `${meta.accent}0d` }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5" style={{ color: meta.accent }}>
                <Sparkles size={12} /> {t.productsSection.onlyLabel}
              </p>
              <p className="text-white/85 text-sm leading-relaxed">{product.onlyUs}</p>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Products() {
  const { t } = useLang();
  return (
    <section id="products" className="relative py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-brand mb-5">
              {t.productsSection.overline}
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
              {t.productsSection.title}
            </h2>
            <p className="mt-6 text-white/65 text-lg font-light max-w-2xl">{t.productsSection.sub}</p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {t.products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
